/**
 * RejectModal Component
 * Modal for rejecting a comment with a reason.
 */
import React, { useState, useEffect } from 'react';
import type { Comment } from '../../types';

interface RejectModalProps {
  comment: Comment;
  isOpen: boolean;
  onReject: (commentId: string, reason: string) => Promise<void>;
  onClose: () => void;
}

const COMMON_REASONS = [
  'This is actually acceptable in this context',
  'This is a stylistic choice, not an error',
  'This is correct for the discipline',
  'The student addressed this elsewhere',
  'This is too minor to flag',
];

const RejectModal: React.FC<RejectModalProps> = ({
  comment,
  isOpen,
  onReject,
  onClose,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setCustomReason('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const finalReason =
      selectedReason === 'custom' ? customReason.trim() : selectedReason;

    if (!finalReason) return;

    setIsRejecting(true);
    try {
      await onReject(comment.id, finalReason);
      onClose();
    } catch {
      // Error is handled by the hook
    } finally {
      setIsRejecting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      if (selectedReason && (selectedReason !== 'custom' || customReason.trim())) {
        handleSubmit();
      }
    }
  };

  const isSubmitDisabled =
    isRejecting ||
    !selectedReason ||
    (selectedReason === 'custom' && !customReason.trim());

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 animate-backdrop-enter"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden animate-modal-enter"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Why is this feedback not appropriate?
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {/* Highlighted text */}
          <div className="mb-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
            <p className="text-sm font-medium text-gray-700 mb-1">
              Highlighted text:
            </p>
            <p className="text-sm text-gray-900 italic">
              &quot;{comment.highlightedText}&quot;
            </p>
          </div>

          {/* AI suggestion */}
          <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-sm font-medium text-gray-700 mb-1">
              AI suggested:
            </p>
            <p className="text-sm text-gray-900">{comment.suggestion}</p>
          </div>

          {/* Reason options */}
          <div className="space-y-2">
            {COMMON_REASONS.map((reason, index) => (
              <label
                key={index}
                className={`flex items-start p-3 rounded-md border cursor-pointer transition-colors ${
                  selectedReason === reason
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="reason"
                  value={reason}
                  checked={selectedReason === reason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm text-gray-900">{reason}</span>
              </label>
            ))}

            {/* Custom reason option */}
            <label
              className={`flex items-start p-3 rounded-md border cursor-pointer transition-colors ${
                selectedReason === 'custom'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="reason"
                value="custom"
                checked={selectedReason === 'custom'}
                onChange={(e) => setSelectedReason(e.target.value)}
                className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm text-gray-900">
                Other (specify below)
              </span>
            </label>

            {selectedReason === 'custom' && (
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Explain why this feedback is not appropriate..."
                rows={3}
                className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                autoFocus
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isRejecting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRejecting ? 'Rejecting...' : 'Reject with Reason'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectModal;
